/**
 * API Retry Utilities
 *
 * Provides retry logic with exponential backoff for transient API failures.
 */

import { isRetryableError } from './clientErrors';

// Default retry configuration
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

/**
 * Calculates delay with exponential backoff and jitter
 * @param {number} attempt - Current attempt number (0-indexed)
 * @param {Object} config - Retry configuration
 * @returns {number} Delay in milliseconds
 */
const calculateDelay = (attempt, config) => {
  const { baseDelayMs, maxDelayMs, backoffMultiplier } = config;

  // Exponential backoff: baseDelay * multiplier^attempt
  const exponentialDelay = baseDelayMs * backoffMultiplier ** attempt;

  // Add jitter (±25% randomization) to prevent thundering herd
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
  const delayWithJitter = exponentialDelay + jitter;

  // Cap at maxDelay
  return Math.min(delayWithJitter, maxDelayMs);
};

/**
 * Delays execution for specified milliseconds
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} Promise that resolves after delay
 */
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Wraps an async function with retry logic
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.baseDelayMs - Base delay in ms (default: 1000)
 * @param {number} options.maxDelayMs - Maximum delay in ms (default: 10000)
 * @param {number} options.backoffMultiplier - Backoff multiplier (default: 2)
 * @param {Function} options.shouldRetry - Custom retry predicate (default: isRetryableError)
 * @param {Function} options.onRetry - Callback called before each retry
 * @returns {Promise} Result of the function or throws after all retries exhausted
 */
export const withRetry = async (fn, options = {}) => {
  const config = { ...DEFAULT_RETRY_CONFIG, ...options };
  const { maxRetries, shouldRetry = isRetryableError, onRetry } = config;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt < maxRetries && shouldRetry(error)) {
        const delayMs = calculateDelay(attempt, config);

        // Call onRetry callback if provided
        if (onRetry) {
          onRetry({ attempt: attempt + 1, maxRetries, delayMs, error });
        }

        await delay(delayMs);
      } else {
        // Either max retries reached or error is not retryable
        throw error;
      }
    }
  }

  // Should not reach here, but throw last error just in case
  throw lastError;
};

/**
 * Creates a retryable version of an API function
 * @param {Function} apiFn - API function to wrap
 * @param {Object} defaultOptions - Default retry options
 * @returns {Function} Wrapped function with retry logic
 */
export const createRetryableApi = (apiFn, defaultOptions = {}) => {
  return async (params, retryOptions = {}) => {
    const options = { ...defaultOptions, ...retryOptions };
    return withRetry(() => apiFn(params), options);
  };
};

/**
 * Retries multiple operations with individual retry logic
 * @param {Array<Function>} operations - Array of async functions
 * @param {Object} options - Retry options
 * @returns {Promise<Array>} Array of results using Promise.allSettled format
 */
export const retryAllSettled = async (operations, options = {}) => {
  const retryableOperations = operations.map(op => withRetry(op, options).catch(e => Promise.reject(e)));
  return Promise.allSettled(retryableOperations);
};
